import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Heart, X, Clock, Sparkles, Lock, ChevronRight, 
  MessageCircle, Users, Star, Shield, BadgeCheck, Filter, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import NotificationBell from "@/components/NotificationBell";
import { getRecoveryTimeLabel, formatLocation } from "@/utils/recoveryTerminology";
import DayOneBadge from "@/components/DayOneBadge";
import MatchCelebration from "@/components/MatchCelebration";

const MatchesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);
  const [superLikesStatus, setSuperLikesStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const isElite = user?.subscription_tier === "recovery_elite";
  const isPlus = user?.subscription_tier === "recovery_plus" || isElite;

  useEffect(() => {
    fetchMatches();
    fetchStats();
    checkUncelebratedMatches();
    if (isElite) {
      fetchSuperLikesStatus();
    }
  }, [isElite]);

  const checkUncelebratedMatches = async () => {
    try {
      const response = await api.getUncelebratedMatches();
      if (response.data.has_uncelebrated && response.data.matches?.length > 0) {
        const firstMatch = response.data.matches[0];
        setCelebrationData({
          matchedUserId: firstMatch.matched_user_id,
          matchedUserPhoto: firstMatch.matched_user_photo,
          matchedUserName: firstMatch.matched_user_name,
          compatibilityScore: firstMatch.compatibility_score,
        });
        setShowCelebration(true);
      }
    } catch (error) {
      console.error("Failed to check uncelebrated matches:", error);
    }
  };

  const markMatchCelebrated = async (matchedUserId) => {
    try {
      await api.markMatchCelebrated(matchedUserId);
    } catch (error) {
      console.error("Failed to mark match celebrated:", error);
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await api.getDailyMatches();
      console.log('[MatchesPage] Daily matches response:', {
        matches: response.data.matches?.length || 0,
        user_id: response.data.user_id,
        match_count: response.data.match_count
      });
      setMatches(response.data.matches || []);
    } catch (error) {
      console.error("Failed to fetch matches:", error.response?.data || error.message);
      toast.error("Failed to load matches");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.getMatchStats();
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchSuperLikesStatus = async () => {
    try {
      const response = await api.getSuperLikesStatus();
      setSuperLikesStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch super likes status:", error);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchMatches();
    fetchStats();
    checkUncelebratedMatches();
  };

  const handleSuperLike = async (matchedUserId) => {
    setActionLoading(matchedUserId);
    try {
      const matchedUser = matches.find(m => m.matched_user_id === matchedUserId);
      const response = await api.sendSuperLike(matchedUserId);
      
      setSuperLikesStatus(prev => ({
        ...prev,
        super_likes_remaining: response.data.super_likes_remaining,
        can_super_like: response.data.super_likes_remaining > 0
      }));
      
      if (response.data.is_mutual_match) {
        setCelebrationData({
          matchedUserId,
          matchedUserPhoto: matchedUser?.photos?.[0] || null,
          matchedUserName: matchedUser?.name || 'Your match',
          compatibilityScore: matchedUser?.compatibility_score || response.data.compatibility_score,
        });
        setShowCelebration(true);
      } else {
        toast.success(response.data.message, {
          icon: "⭐",
          description: `${response.data.super_likes_remaining} Super Likes remaining today`
        });
      }
      
      setMatches(prev => prev.map(m => 
        m.matched_user_id === matchedUserId 
          ? { ...m, status: response.data.is_mutual_match ? "matched" : "interested", mutual_interest: response.data.is_mutual_match }
          : m
      ));
      setSelectedMatch(null);
      fetchStats();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Super Likes are exclusive to Recovery Elite!", {
          action: { label: "Upgrade", onClick: () => navigate("/subscription") }
        });
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || "No Super Likes remaining today");
      } else {
        toast.error(error.response?.data?.detail || "Something went wrong");
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleExpressInterest = async (matchedUserId) => {
    setActionLoading(matchedUserId);
    try {
      const matchedUser = matches.find(m => m.matched_user_id === matchedUserId);
      const response = await api.expressInterest(matchedUserId);
      
      if (response.data.is_mutual_match) {
        setCelebrationData({
          matchedUserId,
          matchedUserPhoto: matchedUser?.photos?.[0] || null,
          matchedUserName: matchedUser?.name || 'Your match',
          compatibilityScore: matchedUser?.compatibility_score || response.data.compatibility_score,
        });
        setShowCelebration(true);
      } else {
        toast.success(response.data.message);
      }
      
      setMatches(prev => prev.map(m => 
        m.matched_user_id === matchedUserId 
          ? { ...m, status: response.data.is_mutual_match ? "matched" : "interested", mutual_interest: response.data.is_mutual_match }
          : m
      ));
      setSelectedMatch(null);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setActionLoading(null);
    }
  };

  const handlePass = async (matchedUserId) => {
    setActionLoading(matchedUserId);
    try {
      await api.passOnMatch(matchedUserId);
      toast.success("No problem, we'll find better matches");
      setMatches(prev => prev.filter(m => m.matched_user_id !== matchedUserId));
      setSelectedMatch(null);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setActionLoading(null);
    }
  };

  const getCompatibilityColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-[#FAB710]";
    return "text-gray-500";
  };

  const getMatchQualityLabel = (score) => {
    if (score >= 85) return { label: "Exceptional Match", bg: "bg-green-500" };
    if (score >= 70) return { label: "Strong Match", bg: "bg-[#1C3A7A]" };
    if (score >= 50) return { label: "Good Match", bg: "bg-[#FAB710]" };
    return { label: "Match", bg: "bg-gray-500" };
  };

  const handleCelebrationClose = () => {
    if (celebrationData?.matchedUserId) {
      markMatchCelebrated(celebrationData.matchedUserId);
    }
    setShowCelebration(false);
    setCelebrationData(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FAB710]/30 border-t-[#FAB710] rounded-full animate-spin mb-4"></div>
        <p className="text-gray-600">Finding your matches...</p>
      </div>
    );
  }

  const pendingMatches = matches.filter(m => m.status === "pending");

  return (
    <>
      <div className="min-h-screen bg-gray-100 pb-24" data-testid="matches-page">
        {/* Match Celebration Modal */}
        <MatchCelebration
          isOpen={showCelebration}
          onClose={handleCelebrationClose}
          currentUserPhoto={user?.photos?.[0]}
          matchedUserPhoto={celebrationData?.matchedUserPhoto}
          matchedUserName={celebrationData?.matchedUserName}
          compatibilityScore={celebrationData?.compatibilityScore}
          onStartChat={() => {
            if (celebrationData?.matchedUserId) {
              markMatchCelebrated(celebrationData.matchedUserId);
              navigate(`/chat/${celebrationData.matchedUserId}`);
            }
            setShowCelebration(false);
            setCelebrationData(null);
          }}
        />

        <div className="lg:max-w-6xl lg:mx-auto lg:px-8 lg:pt-8">
        {/* Themed Header - Like Mobile */}
        <div className="relative bg-gradient-to-br from-[#1C3A7A] via-[#0d1f3c] to-[#061022] pt-8 pb-8 px-4 rounded-b-[30px] lg:rounded-[30px] overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-white/[0.08]" />
          <div className="absolute bottom-4 -left-6 w-20 h-20 rounded-full bg-white/[0.05]" />
          
          {/* Filter Button - Top Right (Elite only) */}
          <div className="absolute top-4 right-4 flex items-center gap-2">
            {isElite && (
              <button 
                onClick={() => navigate("/filters")}
                className="p-2 hover:bg-white/10 rounded-full transition"
              >
                <Filter className="w-5 h-5 text-white" />
              </button>
            )}
            <div className="bg-white/10 rounded-full">
              <NotificationBell />
            </div>
          </div>

          {/* Centered Header Content */}
          <div className="flex flex-col items-center">
            {/* Icon */}
            <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#FAB710] to-[#FFD966] flex items-center justify-center mb-3 shadow-lg">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            
            <h1 className="text-2xl font-bold text-white drop-shadow-md" data-testid="page-title">
              Daily Matches
            </h1>
            <p className="text-white/80 text-sm mt-1" data-testid="matches-count">
              {pendingMatches.length} curated match{pendingMatches.length !== 1 ? 'es' : ''} today
            </p>

            {/* Count Badge */}
            {stats && stats.mutual_matches > 0 && (
              <div className="flex items-center gap-1.5 bg-[#FAB710] px-4 py-1.5 rounded-full mt-3">
                <Heart className="w-4 h-4 text-[#1C3A7A]" />
                <span className="text-[#1C3A7A] font-bold text-sm">{stats.mutual_matches} Mutual</span>
              </div>
            )}
          </div>
        </div>

        {/* Stats Bar - Floating Card */}
        {stats && (
          <div className="px-4 -mt-4">
            <div className="bg-white rounded-2xl shadow-lg p-4 flex items-center justify-around">
              <div className="text-center">
                <p className="text-xl font-bold text-[#1C3A7A]">{stats.today_matches}</p>
                <p className="text-xs text-gray-500">Today</p>
              </div>
              <div className="w-px h-12 bg-gray-200" />
              <div className="text-center">
                <p className="text-xl font-bold text-[#1C3A7A]">{stats.daily_limit}</p>
                <p className="text-xs text-gray-500">Daily Limit</p>
              </div>
              <div className="w-px h-12 bg-gray-200" />
              <div className="text-center">
                <p className="text-xl font-bold text-[#FAB710]">{stats.interested}</p>
                <p className="text-xs text-gray-500">Interested</p>
              </div>
            </div>
          </div>
        )}

        {/* Refresh Button */}
        <div className="px-4 mt-4">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="w-full flex items-center justify-center gap-2 py-3 bg-white rounded-xl shadow-sm text-[#1C3A7A] font-semibold hover:bg-gray-50 transition disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh Matches'}
          </button>
        </div>

        {/* Matches Grid */}
        <div className="px-4 mt-4">
          {pendingMatches.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[#FAB710]/30 to-[#FAB710]/10 flex items-center justify-center mb-6">
                <Sparkles className="w-12 h-12 text-[#FAB710]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">No Matches Yet</h2>
              <p className="text-gray-500 text-center max-w-xs mb-6">
                We're preparing your curated matches. Check back soon!
              </p>
              <Button
                onClick={handleRefresh}
                className="bg-[#1C3A7A] hover:bg-[#152d5e] text-white px-6"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Matches
              </Button>
            </div>
          ) : (
            /* Matches Grid - 2 columns mobile, wider on desktop */
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-5">
              {pendingMatches.map((match) => {
                const quality = getMatchQualityLabel(match.compatibility_score);
                return (
                  <motion.div
                    key={match.matched_user_id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg"
                  >
                    {/* Image Container */}
                    <div 
                      className="relative aspect-[3/4] cursor-pointer"
                      onClick={() => setSelectedMatch(match)}
                    >
                      {match.photos?.[0] ? (
                        <img
                          src={match.photos[0]}
                          alt={match.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#1C3A7A] to-[#0d1f3c] flex items-center justify-center">
                          <span className="text-5xl font-bold text-white/50">
                            {match.name?.charAt(0) || '?'}
                          </span>
                        </div>
                      )}
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      
                      {/* Quality Badge - Top Left */}
                      <div className={`absolute top-2 left-2 ${quality.bg} px-2 py-1 rounded-lg flex items-center gap-1`}>
                        <Sparkles className="w-3 h-3 text-white" />
                        <span className="text-[10px] font-bold text-white">{match.compatibility_score}%</span>
                      </div>

                      {/* Expires Badge - Top Right */}
                      {match.expires_in_hours && (
                        <div className="absolute top-2 right-2 bg-black/50 px-2 py-1 rounded-lg flex items-center gap-1">
                          <Clock className="w-3 h-3 text-white" />
                          <span className="text-[10px] text-white">{match.expires_in_hours}h</span>
                        </div>
                      )}

                      {/* User Info - Bottom */}
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <h3 className="text-white font-bold text-base">
                          {match.name}, {match.age || '??'}
                        </h3>
                        {match.location && (
                          <p className="text-white/80 text-xs mt-0.5">
                            {formatLocation(match.location)}
                          </p>
                        )}
                        {match.days_clean > 0 && (
                          <div className="inline-block bg-green-500/90 px-2 py-0.5 rounded-full mt-1">
                            <span className="text-[10px] text-white font-semibold">
                              {getRecoveryTimeLabel(match.days_clean, match.recovery_terminology)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex">
                      <button
                        onClick={() => handlePass(match.matched_user_id)}
                        disabled={actionLoading === match.matched_user_id}
                        className="flex-1 py-3 flex items-center justify-center gap-1 text-gray-500 hover:bg-gray-50 transition border-r border-gray-100"
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleExpressInterest(match.matched_user_id)}
                        disabled={actionLoading === match.matched_user_id}
                        className="flex-1 py-3 flex items-center justify-center gap-1 text-green-500 hover:bg-green-50 transition"
                      >
                        <Heart className="w-5 h-5" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
        </div>

        {/* Match Detail Modal */}
        <AnimatePresence>
          {selectedMatch && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4"
              onClick={() => setSelectedMatch(null)}
            >
              <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                className="bg-white rounded-t-3xl sm:rounded-3xl max-w-lg w-full max-h-[85vh] overflow-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header Image */}
                <div className="relative">
                  <div className="aspect-[4/3] bg-gray-200">
                    {selectedMatch.photos?.[0] ? (
                      <img
                        src={selectedMatch.photos[0]}
                        alt={selectedMatch.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#1C3A7A] to-[#0d1f3c] flex items-center justify-center">
                        <span className="text-6xl font-bold text-white/50">
                          {selectedMatch.name?.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Close Button */}
                  <button
                    onClick={() => setSelectedMatch(null)}
                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  {/* Compatibility Badge */}
                  <div className="absolute top-4 left-4 bg-gradient-to-r from-[#1C3A7A] to-[#FAB710] text-white px-4 py-2 rounded-full">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      <span className="font-bold">{selectedMatch.compatibility_score}% Match</span>
                    </div>
                  </div>
                </div>

                {/* Modal Content */}
                <div className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedMatch.name}, {selectedMatch.age}
                  </h2>
                  {selectedMatch.location && (
                    <p className="text-gray-500 mt-1">
                      {formatLocation(selectedMatch.location)}
                    </p>
                  )}

                  {/* Recovery Info */}
                  {selectedMatch.days_clean > 0 && (
                    <div className="flex items-center gap-2 mt-3">
                      <Shield className="w-4 h-4 text-green-500" />
                      <span className="text-green-600 font-semibold">
                        {getRecoveryTimeLabel(selectedMatch.days_clean, selectedMatch.recovery_terminology)}
                      </span>
                    </div>
                  )}

                  {/* Bio */}
                  {selectedMatch.bio && (
                    <p className="text-gray-700 mt-4 leading-relaxed">{selectedMatch.bio}</p>
                  )}

                  {/* Compatibility Breakdown */}
                  {selectedMatch.compatibility_breakdown && (
                    <div className="mt-6">
                      <h3 className="font-semibold text-gray-900 mb-3">Compatibility Breakdown</h3>
                      <div className="space-y-3">
                        {Object.entries(selectedMatch.compatibility_breakdown).map(([key, value]) => (
                          <div key={key}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
                              <span className={`font-semibold ${getCompatibilityColor(value)}`}>{value}%</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${value >= 80 ? 'bg-green-500' : value >= 60 ? 'bg-[#FAB710]' : 'bg-gray-400'}`}
                                style={{ width: `${value}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-6">
                    <Button
                      onClick={() => handlePass(selectedMatch.matched_user_id)}
                      disabled={actionLoading === selectedMatch.matched_user_id}
                      variant="outline"
                      className="flex-1 h-14 text-gray-500 border-2"
                    >
                      <X className="w-5 h-5 mr-2" />
                      Pass
                    </Button>
                    <Button
                      onClick={() => handleExpressInterest(selectedMatch.matched_user_id)}
                      disabled={actionLoading === selectedMatch.matched_user_id}
                      className="flex-1 h-14 bg-green-500 hover:bg-green-600 text-white"
                    >
                      <Heart className="w-5 h-5 mr-2" />
                      Like
                    </Button>
                  </div>

                  {/* Super Like Button for Elite */}
                  {isElite && superLikesStatus?.can_super_like && (
                    <Button
                      onClick={() => handleSuperLike(selectedMatch.matched_user_id)}
                      disabled={actionLoading === selectedMatch.matched_user_id}
                      className="w-full h-14 mt-3 bg-gradient-to-r from-[#FAB710] to-amber-500 hover:from-[#e8a50f] hover:to-amber-600 text-white"
                    >
                      <Star className="w-5 h-5 mr-2 fill-white" />
                      Super Like ({superLikesStatus.super_likes_remaining} left)
                    </Button>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <BottomNav />
      </div>
    </>
  );
};

export default MatchesPage;
